import React, { useEffect } from "react";
import { connect } from "react-redux";
import * as R from "ramda";
import CSSModules from "react-css-modules";
import Select from "react-select";
import { actions, getters } from "../model";
import {
  getIsStandardField,
  getShowLanguageVariants,
  getShowMaxLength,
  getIsValid,
  getCanStandardFieldBeEdited,
  getMaxCharacterMessage
} from "../selectors";
import Options from "./Options";
import TextArea from "components/Global-2016/Forms/TextArea";
import getColumnTypes from "components/Event/Settings/Module/utils/get-column-types";
import Loading from "./Loading";
import StandardField from "./StandardField";
import css from "./style.scss";
import { withRouter } from "react-router";
import { Div, Text1 } from "components/Base";
import { noop } from "utils/General";
import { orgId as getOrgId } from "redux/modules/organization/selectors";
import { eventId as getEventId } from "redux/modules/event/selectors";
import DeleteConfirmation from "components/Global/CRM/Modals/DeleteConfirmation";
import { showModal } from "redux/modules/modal/actions";
import ModalWrapper from "../../../components/Global/Modal/Wrappers/Black";
import { FIELD_LEVEL_EVENT, FIELD_LEVEL_GLOBAL } from "../constants";
import LanguageVariants from "./LanguageVariants";
import { isEventFeatureEnabled } from "redux/modules/event/selectors";
import { FEATURES } from "utils/event-types-and-feature-constants";

const decorate = R.compose(
  withRouter,
  connect(
    (state, props) => {
      const opts = {
        orgId: props.params.eventId
          ? undefined
          : props.params.orgId || getOrgId(state),
        eventId: props.params.orgId
          ? undefined
          : props.params.eventId || getEventId(state)
      };

      return {
        ...opts,
        loading: getters.loading(state, props),
        type: getters.type(state, props),
        error: getters.error(state, props),
        fieldLevel: getters.fieldLevel(state, props),
        description: getters.description(state, props),
        fieldTypes: getColumnTypes(props.moduleId, opts.orgId, opts.eventId),
        name: getters.name(state, props),
        isStandardField: getIsStandardField(state, props),
        canStandardFieldBeEdited: getCanStandardFieldBeEdited(state, props),
        maxLength: getters.maxLength(state, props),
        showMaxLength: getShowMaxLength(state, props),
        showLanguageVariants: getShowLanguageVariants(state, props),
        isValid: getIsValid(state, props),
        disableEventFieldLevel: getters.disableEventFieldLevel(state, props),
        maxCharacterMessage: getMaxCharacterMessage(state, props),
        isEventFeatureEnabled: isEventFeatureEnabled(state),
        eventId: getters.eventId(state)
      };
    },
    {
      init: actions.init,
      handleField: actions.handleField,
      setDescription: actions.setDescription,
      handleTypeChange: actions.handleTypeChange,
      handleSave: actions.handleSave,
      handleDelete: actions.handleDelete,
      updateMaxLength: actions.updateMaxLength,
      showModal
    }
  ),
  CSSModules(css)
);

const Layout = props => {
  const {
    init,
    loading,
    fieldTypes,
    type,
    error,
    fieldLevel,
    setDescription,
    handleField,
    description,
    handleTypeChange,
    onSave,
    hideModal,
    name,
    handleSave,
    addAtPosition,
    fieldId,
    isStandardField,
    canStandardFieldBeEdited,
    updateMaxLength,
    maxLength,
    showMaxLength,
    showLanguageVariants,
    isValid,
    showModal,
    disableEventFieldLevel,
    handleDelete,
    onDelete = noop,
    maxCharacterMessage,
    useLevel = true,
    eventId
  } = props;
  useEffect(() => {
    init({
      ...props
    });
  }, []);

  if (loading) {
    return <Loading />;
  }

  if (isStandardField && !canStandardFieldBeEdited && !showLanguageVariants) {
    return <StandardField />;
  }

  const FIELD_LEVEL_OPTIONS = [
    {
      value: FIELD_LEVEL_GLOBAL,
      label: (
        <>
          <span className="material-icons-outlined">language</span> Global Field
        </>
      )
    },
    disableEventFieldLevel
      ? null
      : {
          value: FIELD_LEVEL_EVENT,
          label: (
            <>
              <span className="material-icons-outlined">event</span> Event Field
            </>
          )
        }
  ].filter(v => v);

  let TYPE_OPTIONS = fieldTypes
    .sort((a, b) => a.name.localeCompare(b.name))
    .map(item => ({ value: item.id, label: item.name }));

  if (type === "wysiwyg") {
    TYPE_OPTIONS = [...TYPE_OPTIONS, { value: "wysiwyg", label: "WYSIWYG" }];
  }

  return (
    <div styleName="modalWrapper">
      <div className="modal-header">
        <div className="modal-title" styleName="title">
          {fieldId ? "Edit" : "Add"} Field
        </div>
      </div>

      <div className="modal-body" styleName="modalBody">
        <div className="modal-body-wrapper">
          {/* field name */}
          <div className="input-wrapper">
            <label styleName="formLabel">
              {"Field Name "}
              <span styleName="required">*</span>
            </label>
            <input
              autoFocus
              key="name"
              styleName="input"
              type="text"
              value={name}
              placeholder="Field Name"
              required="required"
              disabled={!canStandardFieldBeEdited && fieldId}
              onChange={ev =>
                handleField({ field: "name", value: ev.target.value })
              }
            />

            {eventId &&
            showLanguageVariants &&
            isEventFeatureEnabled(FEATURES.LANGUAGES) ? (
              <LanguageVariants />
            ) : null}
          </div>

          {/* field type */}
          <div styleName="reactSelectWrapper" className="input-wrapper">
            <label styleName="formLabel">
              {"Field Type "}
              <span styleName="required">*</span>
            </label>
            {!isStandardField && fieldId && (
              <Text1 color="red" mb={2} style={{ fontSize: "10px" }}>
                Warning: Changing the field type will overwrite existing data
                saved in the field.
              </Text1>
            )}
            <Select
              isDisabled={
                isStandardField || (!canStandardFieldBeEdited && fieldId)
              }
              onChange={opt => handleTypeChange({ type: opt.value })}
              value={TYPE_OPTIONS.find(oo => oo.value === type)}
              options={TYPE_OPTIONS}
            />
          </div>

          <div className="input-wrapper">
            <label styleName="formLabel">Field Description</label>
            <TextArea
              onChange={ev => setDescription(ev.target.value)}
              placeholder="Description of field..."
              rows={2}
              value={description}
              disabled={!canStandardFieldBeEdited && fieldId}
            />
          </div>

          {/* field options */}
          <Options moduleId={props.moduleId} />

          {showMaxLength ? (
            <div className="input-wrapper">
              <label styleName="formLabel">Maximum Length</label>
              <input
                styleName="input"
                type="number"
                max={["text"].includes(type) ? 255 : null}
                min={0}
                value={maxLength}
                onChange={ev => updateMaxLength(ev.target.value)}
                disabled={!canStandardFieldBeEdited && fieldId}
                placeholder={
                  maxCharacterMessage &&
                  maxCharacterMessage.message &&
                  maxCharacterMessage.message.length
                    ? maxCharacterMessage.message
                    : ["wysiwyg"].includes(type)
                    ? "Recommended character limit: 5000"
                    : type === "text"
                    ? "Default limit: 255"
                    : null
                }
              />
              {maxCharacterMessage && maxCharacterMessage.error ? (
                <Text1 color="red" mt={2} style={{ fontSize: "10px" }}>
                  You have reached the maximum character limit for this field
                </Text1>
              ) : null}
            </div>
          ) : null}

          {useLevel ? (
            <>
              <div styleName="reactSelectWrapper" className="input-wrapper">
                <label styleName="formLabel">Field Level</label>

                <Select
                  onChange={opt =>
                    handleField({ field: "fieldLevel", value: opt.value })
                  }
                  value={FIELD_LEVEL_OPTIONS.find(
                    oo => oo.value === fieldLevel
                  )}
                  options={FIELD_LEVEL_OPTIONS}
                  isDisabled={
                    isStandardField ||
                    !!fieldId ||
                    (!canStandardFieldBeEdited && fieldId)
                  }
                />
              </div>
              {!fieldId && fieldLevel === FIELD_LEVEL_GLOBAL && (
                <div styleName="fieldLevelSelectWarning">
                  <span class="material-icons-outlined">warning_amber</span>
                  Making this field global will populate its data across all
                  events in the organization.
                </div>
              )}
            </>
          ) : null}

          {isStandardField ? (
            <div styleName="standardField">
              This is a system field provided by Sessionboard. Any changes you
              make will change this field across all events in your
              organization.
            </div>
          ) : null}

          {/* error */}
          {error ? <div styleName="error">{error}</div> : ""}

          {/* actions */}

          <div styleName="actionsWrapper">
            <div styleName="actionsRow">
              <div
                styleName={isValid ? "actionSave" : "actionSaveDisabled"}
                onClick={
                  !isValid
                    ? undefined
                    : () => handleSave({ addAtPosition, fieldLevel, onSave })
                }
                role="button"
                tabIndex="0"
              >
                Save
              </div>
              <div
                styleName="actionCancel"
                onClick={hideModal}
                role="button"
                tabIndex="0"
              >
                Cancel
              </div>
            </div>

            {!isStandardField && fieldId && (
              <Div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  paddingRight: "24px"
                }}
                fs={3}
                color="red7"
                onClick={() =>
                  showModal({
                    content: (
                      <DeleteConfirmation
                        onConfirm={() => {
                          handleDelete({ onSave });
                          onDelete();
                        }}
                        heading="Are You Sure?"
                        confirmLabel="Yes, Delete Field"
                        message={
                          <div>
                            Are you sure you want to delete this field? This
                            will delete the field across any events where it
                            exists. Any data stored in this field from forms,
                            etc will also be deleted.
                          </div>
                        }
                      />
                    ),
                    wrapper: ModalWrapper
                  })
                }
              >
                Delete Field
              </Div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default decorate(Layout);
